import {CrosspointDataProvider, ViewportItem} from "@o2d/datasource";
import {html, nothing} from "lit";
import {get} from "lit-translate";
import {classMap} from "lit/directives/class-map.js";
import {styleMap} from "lit/directives/style-map.js";
import {DateTime, Interval} from "luxon";
import {Issue} from "../../domain/entities/issue.entity";
import {resolveAllEntityColorBars} from "../../helpers/color-bar.resolver";
import {buildAllEntitiesStyleMap} from "../../helpers/entity-color-style.helper";
import {KeyboardActivationHelper} from "../../helpers/keyboard-activation.helper";
import {HeaderItem} from "./types/header-item.type";
import {LocalAreaConfiguration} from "./types/local-area-configuration.interface";
import type {ExtraFixedColumn} from "./types/extra-fixed-column.interface";
import {buildHeaderItemStyleMap} from "../../helpers/header-item-style.helper";
import {rootUidOf} from "../../domain/helpers/issue-type-root.helper";

/** Whole-day comparison (matches the 'today'/'weekend' convention elsewhere in this
 *  file) — true once the issue's end date is strictly before the start of today.
 *  Exported for reuse by resource-planning-area.factory.ts's own .timeline-issue
 *  renderer. */
export const isInPast = (end: DateTime): boolean => end < DateTime.now().startOf('day');

/**
 * Control Area 1 + 2: the entity-color toggle button (grid-area-0's
 * background on/off) and its 5-way color-filter radio group (T/L/E/C/B,
 * applied to grid-area-7). Pulled out into its own factory (2026-08-28)
 * because it turned out to be entirely self-contained — it only ever reaches
 * 'grid-area-0'/'grid-area-7' by their plain DOM ids, never through
 * view-specific data — so every view whose own area config also uses those
 * two ids can compose this same area config in verbatim instead of
 * re-implementing it (currently: createTimelineAreaConfigurations below, and
 * createResourcePlanningAreaConfigurations in
 * resource-planning-area.factory.ts). Single source of truth for this
 * control, one SRP-scoped unit, not duplicated per view.
 *
 * Same "at most one such element in the light DOM at a time" caveat as
 * 'grid-area-0'/'grid-area-7' themselves already carry (see the areaId
 * comments below) — unaffected by this extraction, still true either way.
 *
 * Call contract: call this ONCE per view-configuration build (e.g. once per
 * createXAreaConfigurations() call), not per render — matches how every
 * caller already uses it. `applyColorFilter`/`isColorFilterActive` close
 * over nothing render-specific (they read/write the DOM directly, see their
 * own comments below), so nothing breaks if this contract is violated, but
 * re-invoking per render would pointlessly recreate those closures on every
 * cell render for no behavioral gain.
 */
export const createColorFilterControlAreaConfiguration = (): LocalAreaConfiguration => {
    // grid-area-control1's color-filter radio group (T/L/E/C/B): which kind is
    // currently applied to grid-area-7, or none.
    const COLOR_FILTER_KINDS = ['type', 'label', 'employee', 'creator', 'board'] as const;
    const SHOW_COLOR_CLASSES = COLOR_FILTER_KINDS.map(kind => `show-color-${kind}`);

    const applyColorFilter = (kind: typeof COLOR_FILTER_KINDS[number] | null) => {
        const target = document.getElementById('grid-area-7');
        target?.classList.remove(...SHOW_COLOR_CLASSES);
        if (kind) target?.classList.add(`show-color-${kind}`);
    };

    // "Is this kind currently applied?" is read straight from grid-area-7's own
    // classList — not cached in a JS variable. The actually-visible state (the
    // CSS class, and each radio's native `checked`) lives in the DOM and survives
    // across re-renders on its own; a JS-side "currentFilterKind" closure variable
    // does NOT survive a createTimelineAreaConfigurations() rebuild (this view's
    // filter-subscription callback calls it again on every appState filter
    // change, see abstract-standard-timeline.view.ts), which would silently
    // desync from the still-applied DOM state and break the next deselect click.
    // Reading the DOM directly makes it the only source of truth, so there's
    // nothing to desync.
    const isColorFilterActive = (kind: typeof COLOR_FILTER_KINDS[number]): boolean =>
        document.getElementById('grid-area-7')?.classList.contains(`show-color-${kind}`) ?? false;

    return {
        // Control Area 1: toggle button that turns the entity-color background
        // on grid-area-0 (row-header name cell) on/off via the "without-coloring" class.
        //
        // Intentional technical debt: reaches out to grid-area-0 via
        // document.getElementById(...) instead of Lit state / the shared
        // click-delegation used by grid-area-1/grid-area-2 in this same file.
        // Known risk (tracked separately, forwarded for follow-up): 'grid-area-0'
        // is also used as an areaId in resource-planning-area.factory.ts, so this
        // relies on at most one such element being in the light DOM at a time.
        areaId: 'grid-area-control1',
        sectionIds: {
            columnSectionId: 'column-section-0',
            rowSectionId: 'row-section-3'
        },
        cellRenderer: () => {
            // Toggles the entity-color background AND, on the button itself, an
            // 'is-active' class the icon's ::before content keys off (see
            // timeline.css) to swap between the two icon glyphs (f1fc / e74e) in
            // sync with the coloring state.
            const toggleColoring = (e: Event) => {
                document.getElementById('grid-area-0')?.classList.toggle('without-coloring');
                (e.currentTarget as HTMLElement).classList.toggle('is-active');
            };

            // Default color filter: "type" is preselected on first load. A
            // data-attribute marker on grid-area-7 itself (not a JS variable —
            // see the isColorFilterActive() comment above for why JS-side state
            // doesn't survive createTimelineAreaConfigurations() rebuilds)
            // ensures this runs exactly once per element lifetime: once the
            // marker is set, later re-renders of this control area never touch
            // it again, so a user's later deselect click is never silently
            // overwritten by this default re-applying itself.
            const grid7 = typeof document !== 'undefined' ? document.getElementById('grid-area-7') : null;
            if (grid7 && !grid7.dataset.colorFilterDefaulted) {
                grid7.dataset.colorFilterDefaulted = 'true';
                grid7.classList.add('show-color-type');
            }

            // Radio group (T/L/E/C/B): exactly one "show-color-<kind>" class on
            // #grid-area-7 at a time, or none (applyColorFilter/isColorFilterActive
            // are declared once above, outside this per-render cellRenderer, and
            // read/write the DOM directly rather than caching state in JS — see the
            // comment there for why).
            //
            // click AND change are both bound (not click alone): a plain click or a
            // Space-key activation on the currently-focused radio always fires
            // click, but native radio-group arrow-key navigation is the one
            // interaction where browsers are NOT consistent about also firing click
            // on the newly-selected radio — change always fires there regardless.
            // applyColorFilter() is idempotent, so if both happen to fire for the
            // same interaction that's harmless.
            //
            // Deselecting by clicking the already-selected radio is the one thing
            // that needs the isColorFilterActive() check below: native radios can't
            // be unchecked by re-clicking on their own, and a plain click on an
            // already-checked radio fires click but never change (no state
            // transition happened), so only the click handler ever sees that case.
            //
            // Intentional technical debt — a SECOND, independent instance of the
            // same DOM-ID-collision pattern as the X toggle above (tracked
            // separately, see the X toggle's own comment + its forwarded
            // follow-up): reaches grid-area-7 via document.getElementById(...)
            // instead of Lit state. 'grid-area-7' is also used as an areaId in
            // resource-planning-area.factory.ts, which itself renders
            // .timeline-issue divs — if both views were ever in the light DOM at
            // once and this hit the wrong element, the damage here is more than
            // a silent mis-click: it would visibly recolor Resource Planning's
            // issues instead of Timeline's. Also forwarded as its own follow-up
            // (see .claude/stefan-todos/).
            const onColorFilterChange = (kind: typeof COLOR_FILTER_KINDS[number]) => () => applyColorFilter(kind);

            const onColorFilterClick = (kind: typeof COLOR_FILTER_KINDS[number]) => (e: Event) => {
                if (isColorFilterActive(kind)) {
                    (e.currentTarget as HTMLInputElement).checked = false;
                    applyColorFilter(null);
                } else {
                    applyColorFilter(kind);
                }
            };

            return html`<div
                class="control-area-1"
                role="button"
                tabindex="0"
                title=${get('ha-boards-view.color-filter.toggle')}
                @click=${toggleColoring}
                @keydown=${KeyboardActivationHelper.onEnterOrSpace(toggleColoring)}
            ></div>
            <div class="control-area-2">
            <label class="control-area-2-board" title=${get('ha-boards-view.color-filter.board')}>
                <input type="radio" name="grid-area-7-color-filter" value="board" @change=${onColorFilterChange('board')} @click=${onColorFilterClick('board')}>
            </label>
            <label class="control-area-2-creator" title=${get('ha-boards-view.color-filter.creator')}>
                <input type="radio" name="grid-area-7-color-filter" value="creator" @change=${onColorFilterChange('creator')} @click=${onColorFilterClick('creator')}>
            </label>
            <label class="control-area-2-employee" title=${get('ha-boards-view.color-filter.employee')}>
                <input type="radio" name="grid-area-7-color-filter" value="employee" @change=${onColorFilterChange('employee')} @click=${onColorFilterClick('employee')}>
            </label>
            <label class="control-area-2-label" title=${get('ha-boards-view.color-filter.label')}>
                <input type="radio" name="grid-area-7-color-filter" value="label" @change=${onColorFilterChange('label')} @click=${onColorFilterClick('label')}>
            </label>
            <label class="control-area-2-type" title=${get('ha-boards-view.color-filter.type')}>
                <input type="radio" name="grid-area-7-color-filter" value="type" checked @change=${onColorFilterChange('type')} @click=${onColorFilterClick('type')}>
            </label>
            </div>`;
        }
    };
};

/**
 * Creates standard area configurations for all timeline views.
 * Defines cell renderers for row headers, X-axis headers, and content.
 *
 * `grid-area-7`/`grid-area-calendar-content` resolve color-bar data for a
 * fixed set of entities (type, employee, board, labels, creator — see
 * [[resolveAllEntityColorBars]]) — same for every view, no per-view
 * configuration needed.
 */
export const createTimelineAreaConfigurations = (
    isHighlighted: (uid: string) => boolean,
    isFilteredOut: (issue: Issue) => boolean = () => false,
    extraColumns: ExtraFixedColumn[] = [],
    // What the first column shows for a row; the axis title unless a view renders its own (/by-type).
    rowTitle: (entityUid: string, axisTitle: string) => unknown = (_uid, axisTitle) => axisTitle
): LocalAreaConfiguration[] => {
    // Appended, never inserted, and third in the parameter list on purpose: existing tests
    // read argument index 1 as the isFilteredOut predicate.
    const extraColumnAreas: LocalAreaConfiguration[] = extraColumns.map(column => ({
        areaId: column.areaId,
        sectionIds: {
            columnSectionId: column.sectionId,
            // Always the entity row. See ExtraFixedColumn's own note on why this is fixed
            // here rather than declared per column — the row-header item's span depends on
            // every fixed column having an area in exactly this row.
            rowSectionId: 'row-section-4'
        },
        classes: column.classes,
        cellRenderer: column.cellRenderer
    }));

    return [
        ...extraColumnAreas,
    {
        // Row header: Entity name with background color
        areaId: 'grid-area-0',
        sectionIds: {
            columnSectionId: 'column-section-0',
            rowSectionId: 'row-section-4'
        },
        classes: ["y-axis", "y-axis-title"],
        cellRenderer: (item: ViewportItem<unknown>) => {
            const data = item.data as HeaderItem;
            const uid = data.entities?.[0]?.uid ?? '';
            const classes = {'employee-name': true, highlighted: isHighlighted(uid)};
            return html`<span class=${classMap(classes)} style=${styleMap(buildHeaderItemStyleMap(data.color, data.textColor ?? null))} data-role="highlight" data-entity-uid="${uid}">${rowTitle(uid, data.title)}</span>`;
        }
    },
    {
        // Row header: Add-issue button (opens the create dialog with this row's entity pre-filled)
        areaId: 'grid-area-1',
        sectionIds: {
            columnSectionId: 'column-section-1',
            rowSectionId: 'row-section-4'
        },
        classes: ["y-axis"],
        cellRenderer: (item: ViewportItem<unknown>) => {
            const data = item.data as HeaderItem;
            const uid = data.entities?.[0]?.uid ?? '';
            return html`<div class="btn" data-role="add-issue" data-entity-uid="${uid}" title=${get('ha-boards-view.row-title.new-issue')}><i class="icon icon-plus"></i></div>`;
        }
    },
    {
        // Row header: Context menu button with entity UID for click delegation
        areaId: 'grid-area-2',
        sectionIds: {
            columnSectionId: 'column-section-2',
            rowSectionId: 'row-section-4'
        },
        classes: ["y-axis"],
        cellRenderer: (item: ViewportItem<unknown>) => {
            const data = item.data as HeaderItem;
            const uid = data.entities?.[0]?.uid ?? '';
            return html`<div class="btn" data-role="context-menu" data-entity-uid="${uid}">&#x22EE;</div>`;
        }
    },
    {
        // X-axis header: Month/Year
        areaId: 'grid-area-3',
        sectionIds: {
            columnSectionId: 'column-section-3',
            rowSectionId: 'row-section-0'
        },
        classes: ["x-axis"],
        cellRenderer: (item: ViewportItem<unknown>, dataProvider: CrosspointDataProvider<unknown, unknown, unknown>) => {
            const [minIndex] = item.visibleIndexRangeX;
            const startDate = String(dataProvider.axisX.getValue(minIndex));

            if (!startDate) return html``;

            // Each cell already represents exactly one month from the generator
            const dt = DateTime.fromISO(startDate);
            const monthName = dt.toFormat('LLLL', {locale: 'de'});
            const year = dt.year;
            return html`<span class="month-header">${monthName} ${year}</span>`;
        }
    },
    {
        // X-axis header: Calendar week
        areaId: 'grid-area-4',
        sectionIds: {
            columnSectionId: 'column-section-3',
            rowSectionId: 'row-section-1'
        },
        classes: ["x-axis"],
        cellRenderer: (item: ViewportItem<unknown>, dataProvider: CrosspointDataProvider<unknown, unknown, unknown>) => {
            const [minIndex, maxIndex] = item.visibleIndexRangeX;
            const startDate = dataProvider.axisX.getValue(minIndex) as string;
            const endDate = dataProvider.axisX.getValue(maxIndex) as string;

            if (!startDate || !endDate) return html``;

            const visibleInterval = Interval.fromDateTimes(
                DateTime.fromISO(startDate),
                DateTime.fromISO(endDate).plus({days: 1})
            );
            const weeks = visibleInterval.splitBy({weeks: 1}).map(weekInterval =>
                html`<span class="week-header">KW ${weekInterval.start!.localWeekNumber}</span>`
            );
            return html`${weeks}`;
        }
    },
    {
        // X-axis header: Weekday
        areaId: 'grid-area-5',
        sectionIds: {
            columnSectionId: 'column-section-3',
            rowSectionId: 'row-section-2'
        },
        classes: ["x-axis"],
        cellRenderer: (item: ViewportItem<unknown>, dataProvider: CrosspointDataProvider<unknown, unknown, unknown>) => {
            const [minIndex, maxIndex] = item.visibleIndexRangeX;
            const startDate = dataProvider.axisX.getValue(minIndex) as string;
            const endDate = dataProvider.axisX.getValue(maxIndex) as string;

            if (!startDate || !endDate) return html``;

            const visibleInterval = Interval.fromDateTimes(
                DateTime.fromISO(startDate),
                DateTime.fromISO(endDate).plus({days: 1})
            );
            const days = visibleInterval.splitBy({day: 1}).map(dayInterval => {
                const isWeekend = dayInterval.start!.weekday >= 6;
                const isToday = dayInterval.start!.hasSame(DateTime.now(), 'day');
                const classes = ['weekday-header', isWeekend && 'weekend', isToday && 'today'].filter(Boolean).join(' ');
                return html`<span class="${classes}">${dayInterval.start!.weekdayShort}</span>`;
            });
            return html`${days}`;
        }
    },
    {
        // X-axis header: Day of month
        areaId: 'grid-area-6',
        sectionIds: {
            columnSectionId: 'column-section-3',
            rowSectionId: 'row-section-3'
        },
        classes: ["x-axis"],
        cellRenderer: (item: ViewportItem<unknown>, dataProvider: CrosspointDataProvider<unknown, unknown, unknown>) => {
            const [minIndex, maxIndex] = item.visibleIndexRangeX;
            const startDate = dataProvider.axisX.getValue(minIndex) as string;
            const endDate = dataProvider.axisX.getValue(maxIndex) as string;

            if (!startDate || !endDate) return html``;

            const visibleInterval = Interval.fromDateTimes(
                DateTime.fromISO(startDate),
                DateTime.fromISO(endDate).plus({days: 1})
            );
            const days = visibleInterval.splitBy({day: 1}).map(dayInterval => {
                const isWeekend = dayInterval.start!.weekday >= 6;
                const isToday = dayInterval.start!.hasSame(DateTime.now(), 'day');
                const classes = ['day-header', isWeekend && 'weekend', isToday && 'today'].filter(Boolean).join(' ');
                return html`<span class="${classes}">${dayInterval.start!.day}</span>`;
            });
            return html`${days}`;
        }
    },
    {
        // Content area: Timeline issues with weekend column underlays
        areaId: 'grid-area-7',
        sectionIds: {
            columnSectionId: 'column-section-3',
            rowSectionId: 'row-section-4'
        },
        enableClustering: true,
        showBackgroundGrid: true,
        enableCellClickEvent: true,
        enableCellHoverEvent: true,
        /**
         * Highlights columns based on date type: weekends (Saturday/Sunday) get 'weekend' class,
         * today gets 'today' class. Classes are additive — a Saturday that is today gets both.
         */
        /**
         * Highlights rows where the entity is marked via the highlight toggle button.
         */
        rowUnderlayRenderer: (_yIndex: number, yValue: unknown) => {
            const uid = yValue as string;
            if (!isHighlighted(uid)) return null;
            return { classes: { highlighted: true } };
        },
        columnUnderlayRenderer: (_xIndex: number, xValue: unknown) => {
            const date = DateTime.fromISO(xValue as string);
            const classes: Record<string, boolean> = {};
            if (date.weekday >= 6) classes['weekend'] = true;
            if (date.hasSame(DateTime.now(), 'day')) classes['today'] = true;
            return Object.keys(classes).length > 0 ? { classes } : null;
        },
        cellRenderer: (item: ViewportItem<unknown>) => {
            const data = item.data as Issue;
            const classes = {'timeline-issue': true, 'filtered-out': isFilteredOut(data), 'is-in-past': isInPast(data.end)};
            const barsByKind = resolveAllEntityColorBars(data);
            return html`<div
                class=${classMap(classes)}
                data-sid=${data.uid}
                data-status=${data.status?.key ?? nothing}
                data-root-type=${rootUidOf(data.type) ?? nothing}
                data-abbreviation-type-1=${barsByKind.type[0]?.shortcut ?? nothing}
                style=${styleMap(buildAllEntitiesStyleMap(barsByKind))}><span>${data.title}</span></div>`;
        }
    },
    {
        // Calendar row: Entity name label — same renderer as grid-area-0 but for row-section-calendar
        areaId: 'grid-area-calendar-label',
        sectionIds: {
            columnSectionId: 'column-section-0',
            rowSectionId: 'row-section-calendar'
        },
        classes: ["y-axis", "y-axis-title"],
        cellRenderer: (item: ViewportItem<unknown>) => {
            const data = item.data as HeaderItem;
            const uid = data.entities?.[0]?.uid ?? '';
            const classes = {'employee-name': true, highlighted: isHighlighted(uid)};
            return html`<span class=${classMap(classes)} style=${styleMap(buildHeaderItemStyleMap(data.color, data.textColor ?? null))} data-role="highlight" data-entity-uid="${uid}">${data.title}</span>`;
        }
    },
    {
        // Calendar row: Add-issue button — same renderer as grid-area-1 but for row-section-calendar
        areaId: 'grid-area-calendar-highlight',
        sectionIds: {
            columnSectionId: 'column-section-1',
            rowSectionId: 'row-section-calendar'
        },
        classes: ["y-axis"],
        cellRenderer: (item: ViewportItem<unknown>) => {
            const data = item.data as HeaderItem;
            const uid = data.entities?.[0]?.uid ?? '';
            return html`<div class="btn" data-role="add-issue" data-entity-uid="${uid}" title=${get('ha-boards-view.row-title.new-issue')}><i class="icon icon-plus"></i></div>`;
        }
    },
    {
        // Calendar row: Context menu button — same renderer as grid-area-2 but for row-section-calendar
        areaId: 'grid-area-calendar-context',
        sectionIds: {
            columnSectionId: 'column-section-2',
            rowSectionId: 'row-section-calendar'
        },
        classes: ["y-axis"],
        cellRenderer: (item: ViewportItem<unknown>) => {
            const data = item.data as HeaderItem;
            const uid = data.entities?.[0]?.uid ?? '';
            return html`<div class="btn" data-role="context-menu" data-entity-uid="${uid}">&#x22EE;</div>`;
        }
    },
    {
        // Calendar row: Issue bars — same renderer as grid-area-7 but WITHOUT isFilteredOut predicate
        // Calendar issues (vacations, holidays) are always visible regardless of active filters
        areaId: 'grid-area-calendar-content',
        sectionIds: {
            columnSectionId: 'column-section-3',
            rowSectionId: 'row-section-calendar'
        },
        enableClustering: true,
        showBackgroundGrid: true,
        enableCellClickEvent: true,
        enableCellHoverEvent: true,
        /**
         * Highlights columns based on date type: weekends (Saturday/Sunday) get 'weekend' class,
         * today gets 'today' class. Classes are additive — a Saturday that is today gets both.
         */
        columnUnderlayRenderer: (_xIndex: number, xValue: unknown) => {
            const date = DateTime.fromISO(xValue as string);
            const classes: Record<string, boolean> = {};
            if (date.weekday >= 6) classes['weekend'] = true;
            if (date.hasSame(DateTime.now(), 'day')) classes['today'] = true;
            return Object.keys(classes).length > 0 ? { classes } : null;
        },
        cellRenderer: (item: ViewportItem<unknown>) => {
            const data = item.data as Issue;
            const classes = {'timeline-issue': true, 'is-in-past': isInPast(data.end)};
            const barsByKind = resolveAllEntityColorBars(data);
            return html`<div
                class=${classMap(classes)}
                data-sid=${data.uid}
                data-status=${data.status?.key ?? nothing}
                data-root-type=${rootUidOf(data.type) ?? nothing}
                data-abbreviation-type-1=${barsByKind.type[0]?.shortcut ?? nothing}
                style=${styleMap(buildAllEntitiesStyleMap(barsByKind))}><span>${data.title}</span></div>`;
        }
    },
    createColorFilterControlAreaConfiguration()
    ];
};
